import { type ReactNode, useState } from "react";
import { Check as CheckIcon, Users as UsersIcon } from "@tamagui/lucide-icons";
import { Avatar, type AvatarProps, Badge, Card, Divider, Icon, IconPill, isWeb, Listbox, ListboxOption, Title5, useMedia, XStack, YStack } from "@medbillai/ui";
import { firstName, money } from "../format";
import type { CoveragePlan, NetworkFigures, Tier } from "../read/readCoverage";
import { left, metCallout, type Standing } from "./tiers";

/** The tier a screen opened on for this plan; a later pick, here or elsewhere, doesn't move it. */
export function useOpenedTier(membershipId: string, tier: Tier): Tier {
  const [opened, setOpened] = useState({
    membershipId,
    tier
  });
  if (opened.membershipId !== membershipId) {
    setOpened({
      membershipId,
      tier
    });
    return tier;
  }
  return opened.tier;
}

/** Picks the member's or the family's figures. */
export function TierPicker({
  plan,
  figures,
  standing,
  tier,
  onPick,
  tone
}: {
  plan: CoveragePlan;
  figures: NetworkFigures;
  standing: Standing;
  tier: Tier;
  onPick: (tier: Tier) => void;
  tone: "dark" | "light";
}) {
  // Native opens the menu as a bottom sheet; it reads like the plan switcher.
  const {
    sm,
    gtSm
  } = useMedia();
  const sheet = !isWeb && sm;
  const name = firstName(plan);
  const label = (t: Tier) => t === "individual" ? name : "Family";
  const pair = figures[standing.kind];
  const figureName = `${figures === plan.inNetwork ? "In network" : "Out of network"} ${standing.kind === "deductible" ? "deductible" : "out-of-pocket max"}`;
  const line = (t: Tier) => {
    const figure = pair[t];
    const rest = left(figure);
    if (figure === null) {
      return undefined;
    }
    // One line in the DS row, so the met amount is left to the card when there's a limit.
    return figure.limit === null || rest === null ? `${figureName}: ${money(figure.met)} met` : `${figureName}: ${rest === 0 ? `met, ${money(figure.limit)}` : `${money(rest)} left of ${money(figure.limit)}`}`;
  };
  return <Listbox aria-label="Whose figures" options={[{
    value: "individual",
    label: label("individual")
  }, {
    value: "family",
    label: label("family")
  }]} value={tier} onValueChange={v => onPick(v === "family" ? "family" : "individual")} matchTriggerWidth={false} align={isWeb && gtSm ? "end" : "start"} sheetHeader={<Title5 render="h2" paddingHorizontal="$sp1.5" paddingBottom="$sp0.75">
          Whose figures
        </Title5>} trigger={<IconPill variant="dropdown" tone={tone} avatar={personAvatar(tier, name)} label={label(tier)} aria-label={`Whose figures: ${label(tier)}`} testID="coverage.tier-picker" />} data-sentry-element="Listbox" data-sentry-component="TierPicker" data-sentry-source-file="TierPicker.tsx">
      <MenuSurface sheet={sheet} data-sentry-element="MenuSurface" data-sentry-source-file="TierPicker.tsx">
        <Listbox.Options renderOption={(option, optionProps) => {
        const t = option.value === "family" ? "family" : "individual";
        const row = <ListboxOption {...optionProps} showSelectedCheck={false} leading={<PersonAvatar tier={t} name={name} />} secondary={line(t)} trailing={<XStack alignItems="center" gap="$sp1">
                    {metCallout(standing.kind, t, pair[t]) ? <Badge size="small" bordered={false} status="brand">
                        Met
                      </Badge> : null}
                    {optionProps.selected ? <Icon icon={CheckIcon} size="$iconMedium" color="$iconBrandStrong" /> : null}
                  </XStack>} />;
        return sheet && t === "family" ? <YStack>
                <Divider indent="$sp2" aria-hidden />
                {row}
              </YStack> : row;
      }} data-sentry-element="Listbox.Options" data-sentry-source-file="TierPicker.tsx" />
      </MenuSurface>
    </Listbox>;
}
const personAvatar = (tier: Tier, name: string): AvatarProps => tier === "individual" ? {
  status: "info",
  initials: name.charAt(0)
} : {
  status: "positive",
  icon: UsersIcon
};
function PersonAvatar({
  tier,
  name
}: {
  tier: Tier;
  name: string;
}) {
  return <Avatar size="small" aria-hidden {...personAvatar(tier, name)} data-sentry-element="Avatar" data-sentry-component="PersonAvatar" data-sentry-source-file="TierPicker.tsx" />;
}

/** On the sheet, the rows sit on a flat card, as in the plan switcher. */
function MenuSurface({
  sheet,
  children
}: {
  sheet: boolean;
  children: ReactNode;
}) {
  return sheet ? <Card variant="elevated" size="large" padding={0} marginHorizontal="$sp1" data-sentry-element="Card" data-sentry-component="MenuSurface" data-sentry-source-file="TierPicker.tsx">
      {children}
    </Card> : children;
}